import React, { useState } from "react";
import PropTypes from "prop-types";
import "./AutomatedOffersForm.scss";
import brand from "../../pages/AutomatedOffers/brands";
import EstimatePanel from "./enroll/EstimatePanel";
import PackageChoices from "./enroll/PackageChoices";
import ProviderSelect from "./enroll/ProviderSelect";
import WebsiteChoices from "./enroll/WebsiteChoices";
import {
	EMAIL_PATTERN,
	formatMoney,
	formatPhoneNumber,
	isValidPhone,
	isValidUrl,
} from "./formUtils";

const {
	authorizationText,
	brands: BRANDS,
	enrollmentUrl,
	jotformFields: JOTFORM_FIELDS,
	jotformId: FORM_ID,
	labels,
	packageOptions,
	providerHint,
	providerOptions,
	termsUrl,
} = brand.form;

const findPackageById = (id) =>
	packageOptions.find((p) => p.id === id) || packageOptions[0];

/** Field ids double as error keys, so the first invalid field can be focused. */
const fieldId = (key) => `ao-${key}`;
const urlKey = (site) => `url-${site.replace(/\s+/g, "-").toLowerCase()}`;

const ERROR_MESSAGES = {
	firstName: "Enter the primary contact's first name.",
	lastName: "Enter the primary contact's last name.",
	email: "Enter a valid email address.",
	phone: "Enter a complete 10-digit phone number.",
	provider: "Select your website provider.",
	authorized: "Check this box to confirm you're authorized and agree to the terms.",
};

/** "Fill enrollment" flow: one JotForm submission per website, then that website's enrollment form. */
function AutomatedOffersForm({ selection, onSelectionChange }) {
	const [contact, setContact] = useState({
		firstName: "",
		lastName: "",
		title: "",
		retailerCode: "",
		email: "",
		phone: "",
	});
	const [urls, setUrls] = useState(() =>
		Object.fromEntries(BRANDS.map((site) => [site, ""])),
	);
	const [provider, setProvider] = useState("");
	const [providerOther, setProviderOther] = useState("");
	const [authorized, setAuthorized] = useState(false);
	const [errors, setErrors] = useState({});
	const [saving, setSaving] = useState(null);
	const [opened, setOpened] = useState({});
	const [groupSubmitted, setGroupSubmitted] = useState(false);

	const selectedPackage = findPackageById(selection.package);
	// Retailer groups have no fixed rate and skip the per-website enrollment.
	const isGroup = selection.package === "group";
	const total = isGroup ? null : selectedPackage.rate * selection.brands.length;

	const clearError = (key) =>
		setErrors((prev) => (prev[key] ? { ...prev, [key]: "" } : prev));

	const handleContactChange = (field) => (e) => {
		const value =
			field === "phone"
				? formatPhoneNumber(e.target.value)
				: e.target.value;
		setContact((prev) => ({ ...prev, [field]: value }));
		clearError(field);
	};

	const toggleBrand = (site) => {
		const brands = selection.brands.includes(site)
			? selection.brands.filter((b) => b !== site)
			: BRANDS.filter((b) => b === site || selection.brands.includes(b));
		onSelectionChange({ ...selection, brands });
	};

	/** Pass a website to also check its fields; omit it for a group submission. */
	const validate = (site) => {
		const next = {};
		if (!contact.firstName.trim()) next.firstName = ERROR_MESSAGES.firstName;
		if (!contact.lastName.trim()) next.lastName = ERROR_MESSAGES.lastName;
		if (!EMAIL_PATTERN.test(contact.email.trim()))
			next.email = ERROR_MESSAGES.email;
		if (!isValidPhone(contact.phone)) next.phone = ERROR_MESSAGES.phone;
		if (site && !provider) next.provider = ERROR_MESSAGES.provider;
		// Same order as on screen, so the first invalid field gets focus.
		if (site && !isValidUrl(urls[site].trim()))
			next[urlKey(site)] =
				`Enter the full ${site} website address, starting with https://`;
		if (!authorized) next.authorized = ERROR_MESSAGES.authorized;

		setErrors(next);
		const firstInvalid = Object.keys(next)[0];
		if (firstInvalid)
			document.getElementById(fieldId(firstInvalid))?.focus();
		return !firstInvalid;
	};

	const buildSubmission = (site) => {
		const contactValues = {
			firstName: contact.firstName.trim(),
			lastName: contact.lastName.trim(),
			title: contact.title.trim(),
			retailerCode: contact.retailerCode.trim(),
			email: contact.email.trim(),
			phone: contact.phone,
			package: selectedPackage.label,
			authorized: "Yes",
		};
		const values = site
			? {
					...contactValues,
					ratePerSite: formatMoney(selectedPackage.rate),
					brand: site,
					websiteUrl: urls[site].trim(),
					provider:
						provider === "Other"
							? `Other: ${providerOther.trim()}`
							: provider,
					websitesSelected: selection.brands.join(" + "),
					monthlyEstimate: formatMoney(total),
				}
			: {
					...contactValues,
					ratePerSite: "Volume rate",
					monthlyEstimate: "Confirmed at contract",
				};

		const data = new FormData();
		data.append("formID", FORM_ID);
		data.append("simple_spc", `${FORM_ID}-${FORM_ID}`);
		Object.entries(values).forEach(([key, value]) =>
			data.append(JOTFORM_FIELDS[key], value),
		);
		return data;
	};

	const submitToJotForm = async (site) => {
		try {
			await fetch(`https://submit.jotform.com/submit/${FORM_ID}`, {
				method: "POST",
				mode: "no-cors",
				keepalive: true,
				body: buildSubmission(site),
			});
		} catch (e) {
			// no-cors responses are opaque; continue either way.
		}
	};

	const handleGroupSubmit = async () => {
		if (!validate()) return;
		setSaving("group");
		await submitToJotForm();
		setSaving(null);
		setGroupSubmitted(true);
	};

	const prefilledEnrollmentUrl = (site) =>
		enrollmentUrl(site, {
			firstName: contact.firstName,
			lastName: contact.lastName,
			title: contact.title,
			email: contact.email,
			phone: contact.phone,
			website: urls[site],
		});

	/**
	 * "Fill enrollment" is a real link to the website's pre-filled enrollment form.
	 * Invalid form: stop the link. Valid: send the JotForm save (keepalive, so it
	 * finishes in the background) and let the browser open the link.
	 */
	const handleFillEnrollment = (e, site) => {
		if (!validate(site)) {
			e.preventDefault();
			return;
		}
		submitToJotForm(site);
		setOpened((prev) => ({ ...prev, [site]: true }));
	};

	const fieldClass = (key) => `field${errors[key] ? " field--error" : ""}`;

	const renderError = (key) =>
		errors[key] ? (
			<div className="err" id={`${fieldId(key)}-err`}>
				{errors[key]}
			</div>
		) : null;

	const authorization = (
		<>
			<label
				className={`agree ao-agree${errors.authorized ? " field--error" : ""}`}>
				<input
					type="checkbox"
					id={fieldId("authorized")}
					required
					aria-required="true"
					aria-invalid={Boolean(errors.authorized)}
					aria-describedby={
						errors.authorized ? `${fieldId("authorized")}-err` : undefined
					}
					checked={authorized}
					onChange={(e) => {
						setAuthorized(e.target.checked);
						clearError("authorized");
					}}
				/>
				<span>
					{authorizationText} available{" "}
					{termsUrl ? (
						<a href={termsUrl} target="_blank" rel="noopener noreferrer">
							here
						</a>
					) : (
						"here"
					)}
					.<span className="req" aria-hidden="true" />
				</span>
			</label>
			{renderError("authorized")}
		</>
	);

	const textInput = (
		key,
		label,
		{ required, type = "text", ...rest } = {},
	) => (
		<div className={fieldClass(key)}>
			<label
				className={`f${required ? " req" : ""}`}
				htmlFor={fieldId(key)}>
				{label}
			</label>
			<input
				type={type}
				id={fieldId(key)}
				value={contact[key]}
				onChange={handleContactChange(key)}
				aria-invalid={Boolean(errors[key])}
				aria-describedby={
					errors[key] ? `${fieldId(key)}-err` : undefined
				}
				{...rest}
			/>
			{renderError(key)}
		</div>
	);

	return (
		<div className="form-grid">
			<form noValidate onSubmit={(e) => e.preventDefault()}>
				<fieldset>
					<legend>Primary contact</legend>
					<div className="row">
						{textInput("firstName", "First name", {
							required: true,
							autoComplete: "given-name",
						})}
						{textInput("lastName", "Last name", {
							required: true,
							autoComplete: "family-name",
						})}
						{textInput("title", "Title", {
							autoComplete: "organization-title",
							placeholder: "General Manager",
						})}
						{textInput("retailerCode", labels.code)}
						{textInput("email", "Email", {
							required: true,
							type: "email",
							autoComplete: "email",
						})}
						{textInput("phone", "Phone", {
							required: true,
							type: "tel",
							autoComplete: "tel",
							inputMode: "tel",
							placeholder: "(000) 000-0000",
							maxLength: 14,
						})}
					</div>
				</fieldset>

				<fieldset>
					<legend>Package</legend>
					<PackageChoices
						options={packageOptions}
						value={selection.package}
						onChange={(id) =>
							onSelectionChange({ ...selection, package: id })
						}
					/>
				</fieldset>

				{isGroup ? (
					<fieldset>
						{authorization}
						<div className="ao-group-submit">
							<button
								type="button"
								className="btn"
								disabled={saving !== null}
								onClick={handleGroupSubmit}>
								{saving === "group" ? "Submitting…" : "Submit"}
							</button>
							{groupSubmitted && (
								<div className="hint" role="status">
									Thank you. Constellation will reach out to{" "}
									{contact.email.trim()} to confirm your group
									rate and schedule onboarding.
								</div>
							)}
						</div>
					</fieldset>
				) : (
					<fieldset>
						<legend>Websites to enroll</legend>
						<WebsiteChoices
							brands={BRANDS}
							selected={selection.brands}
							onToggle={toggleBrand}
							noun={labels.websiteNoun}
						/>

						<ProviderSelect
							id={fieldId("provider")}
							otherId={fieldId("providerOther")}
							options={providerOptions}
							value={provider}
							other={providerOther}
							hint={providerHint(provider)}
							error={errors.provider}
							onChange={(value) => {
								setProvider(value);
								clearError("provider");
							}}
							onOtherChange={setProviderOther}
						/>

						{selection.brands.length > 0 ? (
							<div className="row site-configs">
								{selection.brands.map((site) => {
									const key = urlKey(site);
									return (
										<div className="site-config" key={site}>
											<div className={fieldClass(key)}>
												<label
													className="f req"
													htmlFor={fieldId(key)}>
													{site} website URL
												</label>
												<input
													type="url"
													id={fieldId(key)}
													value={urls[site]}
													placeholder={labels.urlPlaceholders[site]}
													onChange={(e) => {
														setUrls((prev) => ({
															...prev,
															[site]: e.target.value,
														}));
														clearError(key);
													}}
													aria-invalid={Boolean(
														errors[key],
													)}
												/>
												{renderError(key)}
											</div>
											<a
												className="btn"
												href={prefilledEnrollmentUrl(site)}
												target="_blank"
												rel="noopener noreferrer"
												onClick={(e) =>
													handleFillEnrollment(e, site)
												}>
												Fill enrollment
											</a>
											{opened[site] && (
												<div className="hint" role="status">
													Saved. The {site} enrollment
													form opened in a new tab.{" "}
													<a
														href={prefilledEnrollmentUrl(
															site,
														)}
														target="_blank"
														rel="noreferrer">
														Reopen
													</a>
												</div>
											)}
										</div>
									);
								})}
							</div>
						) : (
							<div className="err">
								Select at least one website to enroll.
							</div>
						)}

						{authorization}
					</fieldset>
				)}
			</form>

			<EstimatePanel
				packageLabel={selectedPackage.label}
				websites={
					isGroup
						? null
						: selection.brands.length
							? selection.brands.join(" + ")
							: "None selected"
				}
				rate={isGroup ? "Volume rate" : formatMoney(selectedPackage.rate)}
				total={isGroup ? "TBD" : formatMoney(total)}
				per={isGroup ? "confirmed at contract" : "per month"}
				fine={`${isGroup ? "Group rates are confirmed with Constellation at contract. " : ""}Estimate only. Third-party website provider fees, if any, are separate. Constellation confirms your final rate.`}
			/>
		</div>
	);
}

AutomatedOffersForm.propTypes = {
	selection: PropTypes.shape({
		package: PropTypes.string.isRequired,
		brands: PropTypes.arrayOf(PropTypes.string).isRequired,
	}).isRequired,
	onSelectionChange: PropTypes.func.isRequired,
};

export default AutomatedOffersForm;
